import { Trans, useLingui } from "@lingui/react/macro";
import type {
  AgentSkillCatalogEntry,
  Bot,
  ComputerStatus,
  Group,
  ThreadSnapshot,
} from "@rakazo/contracts";
import type { AgentImportReader } from "@rakazo/core";
import { Button } from "@rakazo/ui-web";
import { Monitor, Settings, X } from "lucide-react";
import type { ComponentProps, Dispatch, ReactNode, SetStateAction } from "react";
import { ComputerMaintenanceActions } from "../../components/ComputerMaintenanceActions";
import type { Panel } from "../../lib/right-panel-state";
import type { createSpaceRpc } from "../../lib/rpc";
import { computerPanelNeedsMaintenance } from "../../lib/thread-events";
import { CreateGroupForm, GroupSettings } from "../GroupPanel";
import { ResizableSidePanel } from "../ResizableSidePanel";
import { RoutineEditor } from "../RoutineEditor";
import type { SettingsSection } from "../shell-overlay";
import { AgentImportForm } from "./agent-import";
import { BotSettings } from "./bot-panel";
import { firstThreadRoute } from "./use-shell-navigation";

/** The panel beside the chat: its computer, settings, routines, a new group, or an import. */
export function ShellSidePanel({
  canManageAppSettings = false,
  rpc,
  panel,
  setPanel,
  active,
  activeGroup,
  bots,
  groups,
  setBots,
  setGroups,
  navigate,
  refreshBots,
  refreshThread,
  refreshGroupThread,
  refreshActiveThread,
  computer,
  computerOpen,
  booting,
  computerPanel,
  routineEditorProps,
  openBot,
  openCreatedBot,
  droppedAgent,
  setDroppedAgent,
  importFromDialog,
  setCreateBotOpen,
  modelFocusRequest,
  openSettings,
  memoryProviderConfigured,
  setAgentSkills,
  onClearBot,
}: {
  canManageAppSettings?: boolean;
  rpc: ReturnType<typeof createSpaceRpc>;
  panel: Panel;
  setPanel: (panel: Panel) => void;
  active: Bot | undefined;
  activeGroup: Group | undefined;
  bots: Bot[];
  groups: Group[];
  setBots: Dispatch<SetStateAction<Bot[]>>;
  setGroups: Dispatch<SetStateAction<Group[]>>;
  navigate: (path: string, options?: { replace?: boolean }) => void;
  refreshBots: (includeArchived?: boolean) => Promise<void>;
  refreshThread: (botId: string) => Promise<ThreadSnapshot>;
  refreshGroupThread: (groupId: string) => Promise<ThreadSnapshot>;
  refreshActiveThread: () => Promise<void>;
  computer: ComputerStatus | null;
  computerOpen: boolean;
  booting: boolean;
  computerPanel: ReactNode;
  routineEditorProps: ComponentProps<typeof RoutineEditor> | null;
  openBot: (botId: string) => void;
  openCreatedBot: (bot: Bot) => void;
  droppedAgent: AgentImportReader | null;
  setDroppedAgent: (agent: AgentImportReader | null) => void;
  importFromDialog: boolean;
  setCreateBotOpen: (open: boolean) => void;
  modelFocusRequest: number;
  openSettings: (section: SettingsSection) => void;
  memoryProviderConfigured: boolean;
  setAgentSkills: (skills: AgentSkillCatalogEntry[]) => void;
  onClearBot: (bot: Bot) => void;
}) {
  const { t } = useLingui();
  const open = Boolean(
    panel &&
      (active || activeGroup || panel === "import-agent") &&
      !(panel === "settings" && active?.canConfigure === false),
  );

  async function createGroup(input: { name: string; botIds: string[] }) {
    const group = await rpc.groups.create(input);
    setGroups((current) =>
      current.some((item) => item.id === group.id) ? current : [group, ...current],
    );
    navigate(`/app/g/${group.id}`);
    setPanel(null);
    await refreshBots().catch(() => undefined);
  }

  return (
    <ResizableSidePanel open={open} panel={panel ?? "closed"}>
      {open ? (
        <div className="rk-scroll h-full w-full overflow-y-auto px-5 py-[17px]">
          {panel !== "routine" &&
          panel !== "create-group" &&
          panel !== "group-settings" &&
          panel !== "import-agent" ? (
            <div className="mb-4 flex items-center justify-between">
              {!active && panel !== "settings" ? (
                <span className="text-[13.5px] text-muted-foreground">
                  <Trans>Group</Trans>
                </span>
              ) : null}
              <div className="ml-auto flex gap-1">
                {active &&
                panel === "computer" &&
                !computerOpen &&
                computerPanelNeedsMaintenance(computer?.state, booting) ? (
                  <ComputerMaintenanceActions
                    botId={active.id}
                    computer={computer}
                    onChanged={async () => {
                      await refreshThread(active.id);
                    }}
                  />
                ) : null}
                {active && active.canConfigure !== false ? (
                  <Button
                    variant="ghost"
                    size="icon-sm"
                    aria-label={panel === "settings" ? t`Show computer` : t`Show settings`}
                    onClick={() => setPanel(panel === "settings" ? "computer" : "settings")}
                    className={panel === "settings" ? "text-foreground" : "text-muted-foreground"}
                  >
                    {panel === "settings" ? (
                      <Monitor size={16} strokeWidth={1.7} />
                    ) : (
                      <Settings size={16} strokeWidth={1.7} />
                    )}
                  </Button>
                ) : null}
                <Button
                  variant="ghost"
                  size="icon-sm"
                  aria-label={t`Close panel`}
                  onClick={() => setPanel(null)}
                >
                  <X size={16} strokeWidth={1.8} />
                </Button>
              </div>
            </div>
          ) : null}
          {panel === "computer" && active ? computerPanel : null}
          {panel === "create-group" ? (
            <CreateGroupForm
              bots={bots}
              onCancel={() => setPanel(null)}
              onCreate={(input) => createGroup(input)}
              onOpenBot={(botId) => {
                setPanel(null);
                openBot(botId);
              }}
            />
          ) : null}
          {panel === "group-settings" && activeGroup ? (
            <GroupSettings
              key={activeGroup.id}
              group={activeGroup}
              bots={bots}
              onSave={async (input) => {
                const updated = await rpc.groups.update({ groupId: activeGroup.id, ...input });
                setGroups((current) =>
                  current.map((group) => (group.id === updated.id ? updated : group)),
                );
                void Promise.all([refreshBots(), refreshGroupThread(activeGroup.id)]).catch(
                  () => undefined,
                );
              }}
              onRemove={async () => {
                await rpc.groups.remove({ groupId: activeGroup.id });
                const remainingGroups = groups.filter((group) => group.id !== activeGroup.id);
                setGroups(remainingGroups);
                setPanel(null);
                navigate(firstThreadRoute(bots, remainingGroups), { replace: true });
                await refreshBots().catch(() => undefined);
              }}
              onClose={() => setPanel(null)}
              onOpenBot={openBot}
              covered={computerOpen}
            />
          ) : null}
          {panel === "import-agent" ? (
            <AgentImportForm
              bots={bots.filter((bot) => !bot.archivedAt)}
              initialSource={droppedAgent ?? undefined}
              onBack={() => {
                setDroppedAgent(null);
                setPanel(null);
                if (importFromDialog) setCreateBotOpen(true);
              }}
              onCancel={() => {
                setDroppedAgent(null);
                setPanel(null);
              }}
              onCreated={(bot) => {
                setDroppedAgent(null);
                openCreatedBot(bot);
                // An import into an existing bot changes it and its chat.
                void refreshBots().catch(() => undefined);
                if (bot.id === active?.id) void refreshActiveThread();
              }}
            />
          ) : null}
          {panel === "settings" && active && active.canConfigure !== false ? (
            <BotSettings
              key={active.id}
              bot={active}
              canManageAppSettings={canManageAppSettings}
              focusModel={modelFocusRequest}
              onOpenConnections={canManageAppSettings ? () => openSettings("models") : undefined}
              memoryProviderConfigured={memoryProviderConfigured}
              onSkillsChange={setAgentSkills}
              onSave={async ({ computerMode, ...patch }) => {
                if (computerMode !== undefined && computerMode !== active.computerMode) {
                  await rpc.bots.setComputer({
                    botId: active.id,
                    mode: computerMode,
                  });
                }
                const updated = await rpc.bots.update({ botId: active.id, ...patch });
                setBots((current) => current.map((bot) => (bot.id === updated.id ? updated : bot)));
                // Persistence succeeded; a failed navigation refresh must not undo the tool edit.
                await refreshBots().catch(() => undefined);
              }}
              onExport={async () => {
                const manifest = await rpc.export.bot({ botId: active.id });
                const blob = new Blob([JSON.stringify(manifest, null, 2)], {
                  type: "application/json",
                });
                const url = URL.createObjectURL(blob);
                const a = document.createElement("a");
                a.href = url;
                a.download = `${active.name.toLowerCase().replace(/\s+/g, "-")}-export.json`;
                a.click();
                URL.revokeObjectURL(url);
              }}
              onClear={() => onClearBot(active)}
              onUndoImport={() => {
                setPanel(null);
                void refreshBots(true);
                // An import into an existing bot is undone without removing the bot.
                void refreshActiveThread().catch(() => undefined);
              }}
            />
          ) : null}
          {panel === "routine" && routineEditorProps ? (
            <RoutineEditor {...routineEditorProps} />
          ) : null}
        </div>
      ) : null}
    </ResizableSidePanel>
  );
}
